import axios from 'axios';
import { formatDateToISO } from '../utils/dateUtils';

export type TimeSlotName =
  | "morning" | "noon" | "evening" | "bedtime"
  | "h00" | "h02" | "h04" | "h06" | "h08" | "h10"
  | "h12" | "h14" | "h16" | "h18" | "h20" | "h22";

export interface AdministrationRecord {
  treatmentPostId: number;
  patientUuid: string;
  wasAdministered: boolean;
  administrationDate: string; // YYYY-MM-DD format
  timeSlot?: TimeSlotName;
  reasonNotAdministered?: string;
  administeredBy?: string;
}

export interface AdministrationResponse {
  id: number;
  relationType: 'administration';
  sourcePost: {
    id: number;
  };
  relatedPost: {
    id: number;
  };
  patientInvestigation: {
    id: number;
    uuid: string;
  };
  relatedAt: string;
  createdAt: string;
  updatedAt: string;
  fieldValues: {
    wasAdministered: boolean; // Boolean in response
    administrationDate: string;
    timeSlot?: TimeSlotName;
    reasonNotAdministered?: string;
    administeredBy?: string;
  };
}

export interface ApiResponse<T> {
  status: number;
  result?: T;
  errorCode?: number;
  errorMessage?: string;
}

// Error code mappings
export const ERROR_MESSAGES: Record<number, string> = {
  1: 'Missing required fields',
  2: 'Invalid date format',
  3: 'Researcher not found',
  4: 'Treatment record not found',
  5: 'Not a treatment field',
  6: 'Administration field does not exist',
};

// Date formatting is now handled by formatDateToISO utility

/**
 * Parses API response and handles errors
 */
function handleApiResponse<T>(response: any): T {
  const apiResponse: ApiResponse<T> = response.data || response;
  
  if (apiResponse.status !== 200) {
    const errorCode = apiResponse.errorCode || apiResponse.status;
    const errorMessage = apiResponse.errorMessage || ERROR_MESSAGES[errorCode] || 'An error occurred';
    throw new Error(`Error ${errorCode}: ${errorMessage}`);
  }
  
  if (!apiResponse.result) {
    throw new Error('Invalid response: missing result');
  }
  
  return apiResponse.result;
}

export const treatmentAdministrationService = {
  /**
   * Records a treatment administration
   * @param investigationUuid - Investigation UUID
   * @param data - Administration data
   * @returns Promise with administration relation
   */
  recordAdministration: async (
    uuidInvestigation: string,
    data: AdministrationRecord
  ): Promise<AdministrationResponse> => {
    const endpoint = `${import.meta.env.VITE_APP_API_URL}/researcher/investigation/${uuidInvestigation}/treatments/${data.treatmentPostId}/administrations`;
    
    // Request body matches plan: flat structure with administrationDate (YYYY-MM-DD)
    const requestBody = {
      patientUuid: data.patientUuid,
      wasAdministered: data.wasAdministered,
      administrationDate: data.administrationDate, // YYYY-MM-DD format
      timeSlot: data.timeSlot,
      administeredBy: data.administeredBy,
      reasonNotAdministered: data.reasonNotAdministered,
    };
    
    try {
      const response = await axios.post<ApiResponse<AdministrationResponse>>(
        endpoint,
        requestBody,
        { 
          headers: { 
            "Authorization": localStorage.getItem("jwt"),
            "Content-Type": "application/json"
          } 
        }
      );
      
      return handleApiResponse<AdministrationResponse>(response);
    } catch (error: any) {
      
      // Handle structured error response
      if (error.response?.data) {
        const apiError = error.response.data as ApiResponse<never>;
        if (apiError.errorCode) {
          // Handle errorMessage as string or object
          let errorMessage = apiError.errorMessage;
          if (typeof errorMessage === 'object' && errorMessage !== null) {
            errorMessage = JSON.stringify(errorMessage);
          } else if (!errorMessage) {
            errorMessage = ERROR_MESSAGES[apiError.errorCode] || 'Unknown error';
          }
          throw new Error(`Error ${apiError.errorCode}: ${errorMessage}`);
        }
      }
      
      throw error;
    }
  },

  /**
   * Gets all administrations for a patient
   * @param investigationUuid - Investigation UUID
   * @param patientUuid - Patient UUID
   * @param startDate - Optional start date filter (YYYY-MM-DD)
   * @param endDate - Optional end date filter (YYYY-MM-DD)
   * @returns Promise with array of administrations
   */
  getPatientAdministrations: async (
    uuidInvestigation: string,
    patientUuid: string,
    startDate?: string, // YYYY-MM-DD format
    endDate?: string     // YYYY-MM-DD format
  ): Promise<AdministrationResponse[]> => {
    const params = new URLSearchParams();
    if (startDate) params.append('startDate', startDate);
    if (endDate) params.append('endDate', endDate);
    
    const url = `${import.meta.env.VITE_APP_API_URL}/researcher/investigation/${uuidInvestigation}/patients/${patientUuid}/administrations${params.toString() ? `?${params.toString()}` : ''}`;
    
    try {
      const response = await axios.get<ApiResponse<AdministrationResponse[]>>(
        url,
        { headers: { "Authorization": localStorage.getItem("jwt") } }
      );
      
      const result = handleApiResponse<AdministrationResponse[]>(response);
      return Array.isArray(result) ? result : [];
    } catch (error: any) {
      throw error;
    }
  },

  /**
   * Gets administration history for a specific treatment
   * @param investigationUuid - Investigation UUID
   * @param treatmentPostId - Treatment post ID
   * @param startDate - Optional start date filter (YYYY-MM-DD)
   * @param endDate - Optional end date filter (YYYY-MM-DD)
   * @returns Promise with array of administrations
   */
  getTreatmentHistory: async (
    uuidInvestigation: string,
    treatmentPostId: number,
    startDate?: string, // YYYY-MM-DD format
    endDate?: string    // YYYY-MM-DD format
  ): Promise<AdministrationResponse[]> => {
    const params = new URLSearchParams();
    if (startDate) params.append('startDate', startDate);
    if (endDate) params.append('endDate', endDate);
    
    const url = `${import.meta.env.VITE_APP_API_URL}/researcher/investigation/${uuidInvestigation}/treatments/${treatmentPostId}/administrations${params.toString() ? `?${params.toString()}` : ''}`;
    
    try {
      const response = await axios.get<ApiResponse<AdministrationResponse[]>>(
        url,
        { headers: { "Authorization": localStorage.getItem("jwt") } }
      );
      
      const result = handleApiResponse<AdministrationResponse[]>(response);
      return Array.isArray(result) ? result : [];
    } catch (error: any) {
      throw error;
    }
  },

  /**
   * Deletes an administration record
   * @param investigationUuid - Investigation UUID
   * @param treatmentPostId - Treatment post ID
   * @param administrationId - Administration ID to delete
   * @returns Promise with void
   */
  deleteAdministration: async (
    uuidInvestigation: string,
    treatmentPostId: number,
    administrationId: number
  ): Promise<void> => {
    const endpoint = `${import.meta.env.VITE_APP_API_URL}/researcher/investigation/${uuidInvestigation}/treatments/${treatmentPostId}/administrations/${administrationId}`;
    
    try {
      const response = await axios.delete<ApiResponse<void>>(
        endpoint,
        { 
          headers: { 
            "Authorization": localStorage.getItem("jwt"),
            "Content-Type": "application/json"
          } 
        }
      );
      
      handleApiResponse<void>(response);
    } catch (error: any) {
      
      // Handle structured error response
      if (error.response?.data) {
        const apiError = error.response.data as ApiResponse<never>;
        if (apiError.errorCode) {
          let errorMessage = apiError.errorMessage;
          if (typeof errorMessage === 'object' && errorMessage !== null) {
            errorMessage = JSON.stringify(errorMessage);
          } else if (!errorMessage) {
            errorMessage = ERROR_MESSAGES[apiError.errorCode] || 'Unknown error';
          }
          throw new Error(String(apiError.errorCode)); // Show backend error message directly
        }
      }
      
      throw error;
    }
  },

  // Export ERROR_MESSAGES for use in components
  ERROR_MESSAGES,
};
